import { useCallback, useEffect, useRef, useState } from 'react';
import '../styles/manageMentors.css';
import { useCurrentUser } from '../contexts/UserContext';
import {
  getMentorList,
  getMentorEnrollments,
  getUnassignedEnrollments,
  assignMentor,
  unassignMentor,
  createTeacher,
  removeTeacher,
} from '../services/mentorService';
import { listCourses } from '../services/courseService';
import ScheduleMeetingModal from './dashboard/sections/ScheduleMeetingModal';

// ─── helpers ────────────────────────────────────────────────────────────────

function initials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

const STATUS_MAP = {
  active:         { label: 'Active',    cls: 'badge-active' },
  inactive:       { label: 'Inactive',  cls: 'badge-inactive' },
  suspended:      { label: 'Suspended', cls: 'badge-warn' },
  scheduled:      { label: 'Scheduled', cls: 'badge-info' },
  completed:      { label: 'Completed', cls: 'badge-active' },
  dropped:        { label: 'Dropped',   cls: 'badge-warn' },
  archived:       { label: 'Archived',  cls: 'badge-inactive' },
};

function StatusBadge({ status }) {
  const { label, cls } = STATUS_MAP[status] || { label: status || 'Unknown', cls: 'badge-inactive' };
  return <span className={`mm-badge ${cls}`}>{label}</span>;
}

function LoadingDots() {
  return (
    <span className="mm-loading-dots" aria-label="Loading">
      <span /><span /><span />
    </span>
  );
}

// ─── SearchRow ───────────────────────────────────────────────────────────────

function SearchRow({ mentors, value, onChange, loading, onLookup, children }) {
  return (
    <div className="mm-search-row">
      <div className="mm-search-field mm-search-field--typeahead">
        <svg className="mm-search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <MentorTypeahead
          mentors={mentors}
          value={value}
          onChange={onChange}
          placeholder="Search mentors…"
        />
      </div>
      <button className="secondary-btn mm-lookup-btn" onClick={onLookup} disabled={loading || !value}>
        {loading ? <LoadingDots /> : 'Look Up'}
      </button>
      {children}
    </div>
  );
}

// ─── MentorTypeahead ──────────────────────────────────────────────────────────

function MentorTypeahead({ mentors, value, onChange, placeholder = 'Search mentors…' }) {
  const [query, setQuery]       = useState('');
  const [open, setOpen]         = useState(false);
  const [focused, setFocused]   = useState(false);
  const wrapRef                 = useRef(null);

  const selected = mentors.find((m) => m.id === value) || null;

  useEffect(() => {
    function handler(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim()
    ? mentors.filter((m) =>
        m.name.toLowerCase().includes(query.toLowerCase()) ||
        (m.email && m.email.toLowerCase().includes(query.toLowerCase()))
      )
    : mentors;

  const handleSelect = (mentor) => {
    onChange(mentor.id);
    setQuery('');
    setOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setQuery('');
    setOpen(true);
  };

  const displayValue = selected
    ? selected.name + (selected.tagline && selected.tagline !== 'Mentor' ? ` · ${selected.tagline}` : '')
    : query;

  return (
    <div className="mm-typeahead" ref={wrapRef}>
      <div className={`mm-typeahead-input-wrap ${focused ? 'focused' : ''} ${selected ? 'has-value' : ''}`}>
        <input
          className="mm-typeahead-input"
          type="text"
          placeholder={placeholder}
          value={selected ? displayValue : query}
          readOnly={Boolean(selected)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setFocused(true); if (!selected) setOpen(true); }}
          onBlur={() => setFocused(false)}
          autoComplete="off"
        />
        {selected ? (
          <button type="button" className="mm-typeahead-clear" onClick={handleClear} aria-label="Clear">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        ) : (
          <svg className="mm-typeahead-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
      </div>

      {open && !selected && (
        <ul className="mm-typeahead-dropdown">
          {filtered.length === 0 ? (
            <li className="mm-typeahead-empty">No mentors match "{query}"</li>
          ) : (
            filtered.map((m) => (
              <li key={m.id} className="mm-typeahead-option" onMouseDown={() => handleSelect(m)}>
                <span className="mm-typeahead-name">{m.name}</span>
                {m.email && <span className="mm-typeahead-meta">{m.email}</span>}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

// ─── MentorProfileCard ────────────────────────────────────────────────────────

function MentorProfileCard({ mentor, onRemoved, canRemove = true }) {
  const name = mentor.name || `${mentor.firstName || ''} ${mentor.lastName || ''}`.trim();
  const [removeState, setRemoveState] = useState('idle'); // idle | confirm | removing | done
  const [removeData, setRemoveData]   = useState(null);   // { enrollmentCount }
  const [removeError, setRemoveError] = useState('');

  const handleRemoveClick = async () => {
    if (!canRemove) return;
    setRemoveError('');
    setRemoveState('removing');
    try {
      await removeTeacher(mentor.id, false);
      setRemoveState('done');
      onRemoved?.();
    } catch (err) {
      // 409 means they have enrollments — show confirm with count
      if (err.message && err.message.includes('enrollments')) {
        const match = err.message.match(/\d+/);
        setRemoveData({ enrollmentCount: match ? parseInt(match[0], 10) : '?' });
        setRemoveState('confirm');
      } else {
        setRemoveError(err.message || 'Unable to remove mentor.');
        setRemoveState('idle');
      }
    }
  };

  const handleForceRemove = async () => {
    setRemoveState('removing');
    setRemoveError('');
    try {
      await removeTeacher(mentor.id, true);
      setRemoveState('done');
      onRemoved?.();
    } catch (err) {
      setRemoveError(err.message || 'Unable to remove mentor.');
      setRemoveState('idle');
    }
  };

  const disableRemoveBtn = removeState === 'removing' || !canRemove;

  return (
    <div className="mm-card">
      <div className="mm-card-head">
        <div>
          <p className="eyebrow">Mentor profile</p>
        </div>
      </div>

      <div className="mm-profile">
        <div className="mm-avatar">{initials(name)}</div>
        <div className="mm-profile-body">
          <p className="mm-profile-name">{name}</p>
          <p className="mm-profile-email">{mentor.email || mentor.emailAddress}</p>
          <div className="mm-profile-chips">
            <span className="mm-chip">teacher</span>
            <StatusBadge status={mentor.status} />
            {mentor.tagline && <span className="mm-chip">{mentor.tagline}</span>}
          </div>
        </div>
      </div>

      {removeState === 'done' ? (
        <p className="success-text mm-mt-sm">Mentor removed from the system.</p>
      ) : (
        <div className="mm-remove-section">
          {removeState === 'confirm' ? (
            <div className="mm-remove-confirm">
              <p className="mm-remove-warn">
                This mentor has <strong>{removeData?.enrollmentCount}</strong> active enrollment{removeData?.enrollmentCount !== 1 ? 's' : ''}.
                Removing will unassign all of them.
              </p>
              <div className="mm-remove-actions">
                <button className="mm-danger-btn" onClick={handleForceRemove}>
                  Remove anyway
                </button>
                <button className="secondary-btn compact" onClick={() => setRemoveState('idle')}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              className="mm-remove-btn"
              onClick={handleRemoveClick}
              disabled={disableRemoveBtn}
              title={!canRemove ? 'Remove all enrollments before removing this mentor.' : undefined}
            >
              {removeState === 'removing' ? <LoadingDots /> : 'Remove from system'}
            </button>
          )}
          {!canRemove && (
            <p className="mm-muted mm-mt-sm">Remove this mentor&rsquo;s enrollments to enable removal.</p>
          )}
          {removeError && <p className="error-text mm-mt-sm">{removeError}</p>}
        </div>
      )}
    </div>
  );
}

// ─── MentorAssignmentsCard ────────────────────────────────────────────────────

function MentorAssignmentsCard({ teacherId, onCountChange, onRosterChange }) {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [actionError, setActionError] = useState('');
  const [removingId, setRemovingId]   = useState('');

  const loadEnrollments = useCallback(() => {
    if (!teacherId) return;
    setLoading(true);
    setError('');
    getMentorEnrollments(teacherId)
      .then((d) => setEnrollments(d?.enrollments || []))
      .catch((err) => setError(err.message || 'Unable to load assignments.'))
      .finally(() => setLoading(false));
  }, [teacherId]);

  useEffect(() => {
    loadEnrollments();
  }, [loadEnrollments]);

  useEffect(() => {
    onCountChange?.(enrollments.length);
  }, [enrollments, onCountChange]);

  const handleUnassign = async (enrollmentId) => {
    setRemovingId(enrollmentId);
    setActionError('');
    try {
      await unassignMentor(enrollmentId);
      setEnrollments((prev) => prev.filter((e) => e.id !== enrollmentId));
      onRosterChange?.();
    } catch (err) {
      setActionError(err.message || 'Unable to remove enrollment.');
    } finally {
      setRemovingId('');
    }
  };

  return (
    <div className="mm-card mm-assignment-card">
      <div className="mm-card-head">
        <div>
          <p className="eyebrow">Current assignments</p>
        </div>
        <button className="mm-refresh-btn" onClick={loadEnrollments}>Refresh</button>
      </div>

      {actionError && <p className="error-text mm-mt-sm">{actionError}</p>}

      <div className="mm-list mm-list-scroll">
        {loading && <p className="mm-muted">Loading assignments…</p>}
        {!loading && error && <p className="error-text">{error}</p>}
        {!loading && !error && enrollments.length === 0 && (
          <p className="mm-muted">No enrollments assigned to this mentor yet.</p>
        )}
        {enrollments.map((e) => (
          <div key={e.id} className="mm-list-row">
            <div className="mm-list-main">
              <p className="mm-list-title">{e.student?.name || '—'}</p>
              <p className="mm-muted">{e.student?.email}</p>
            </div>
            <div className="mm-list-right">
              <p className="mm-list-course">{e.course?.title || '—'}</p>
              <p className="mm-muted">Grade {e.course?.grade} · {e.course?.subject}</p>
            </div>
            <div className="mm-list-meta">
              <StatusBadge status={e.status} />
              <button
                className="mm-link-btn"
                onClick={() => handleUnassign(e.id)}
                disabled={removingId === e.id}
              >
                {removingId === e.id ? <LoadingDots /> : 'Unassign'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── UnassignedEnrollmentsCard ────────────────────────────────────────────────

function UnassignedEnrollmentsCard({ mentors, mentorsLoading, onAssigned }) {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    getUnassignedEnrollments()
      .then((d) => setEnrollments(d?.enrollments || []))
      .catch((err) => setError(err.message || 'Unable to load unassigned enrollments.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mm-card">
      <div className="mm-card-head">
        <div>
          <p className="eyebrow">Pending assignment</p>
        </div>
        {!loading && (
          <button className="mm-refresh-btn" onClick={load}>Refresh</button>
        )}
      </div>

      <div className="mm-list">
        {loading && <p className="mm-muted">Loading unassigned enrollments…</p>}
        {!loading && error && <p className="error-text">{error}</p>}
        {!loading && !error && enrollments.length === 0 && (
          <p className="mm-muted">All enrollments have a mentor assigned.</p>
        )}
        {enrollments.map((e) => (
          <UnassignedRow
            key={e.id}
            enrollment={e}
            mentors={mentors}
            mentorsLoading={mentorsLoading}
            onAssigned={(updated) => {
              setEnrollments((prev) => prev.filter((x) => x.id !== e.id));
              onAssigned?.(updated);
            }}
          />
        ))}
      </div>
    </div>
  );
}

function UnassignedRow({ enrollment, mentors, mentorsLoading, onAssigned }) {
  const [selectedMentorId, setSelectedMentorId] = useState('');
  const [saving, setSaving]                     = useState(false);
  const [error, setError]                       = useState('');

  const handleAssign = async () => {
    if (!selectedMentorId) return;
    setSaving(true);
    setError('');
    try {
      const data = await assignMentor(enrollment.id, selectedMentorId);
      onAssigned?.(data?.enrollment);
    } catch (err) {
      setError(err.message || 'Unable to assign mentor.');
      setSaving(false);
    }
  };

  return (
    <div className="mm-unassigned-row">
      <div className="mm-unassigned-info">
        <p className="mm-list-title">{enrollment.student?.name || '—'}</p>
        <p className="mm-muted">{enrollment.course?.title} · Grade {enrollment.course?.grade} · {enrollment.course?.subject}</p>
        {error && <p className="error-text mm-mt-sm">{error}</p>}
      </div>
      <div className="mm-unassigned-assign">
        {mentorsLoading ? (
          <p className="mm-muted">Loading…</p>
        ) : (
          <MentorTypeahead
            mentors={mentors}
            value={selectedMentorId}
            onChange={setSelectedMentorId}
            placeholder="Assign mentor…"
          />
        )}
        <button
          className="mm-dark-btn"
          onClick={handleAssign}
          disabled={!selectedMentorId || saving}
        >
          {saving ? <LoadingDots /> : 'Assign'}
        </button>
      </div>
    </div>
  );
}

// ─── MultiSelect (subjects) ───────────────────────────────────────────────────

function SubjectMultiSelect({ allSubjects, selected, onChange }) {
  const [open, setOpen]   = useState(false);
  const wrapRef           = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (subj) => {
    onChange(
      selected.includes(subj) ? selected.filter((s) => s !== subj) : [...selected, subj]
    );
  };

  const displayText = selected.length === 0
    ? 'Select subjects…'
    : selected.join(', ');

  return (
    <div className="mm-multiselect" ref={wrapRef}>
      <button
        type="button"
        className={`mm-multiselect-trigger ${open ? 'open' : ''}`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={selected.length === 0 ? 'mm-multiselect-placeholder' : ''}>
          {displayText}
        </span>
        <svg className="mm-typeahead-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <ul className="mm-multiselect-dropdown">
          {allSubjects.map((subj) => {
            const checked = selected.includes(subj);
            return (
              <li key={subj} className={`mm-multiselect-option ${checked ? 'checked' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); toggle(subj); }}>
                <span className={`mm-checkbox ${checked ? 'checked' : ''}`}>
                  {checked && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </span>
                {subj}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ─── CourseMultiSelect (with typeahead) ───────────────────────────────────────

function CourseMultiSelect({ courses, selected, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const inputRef          = useRef(null);
  const wrapRef           = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedSet = new Set(selected);

  const filtered = courses.filter((c) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      c.title.toLowerCase().includes(q) ||
      c.subject.toLowerCase().includes(q) ||
      String(c.grade).includes(q)
    );
  });

  const toggle = (id) => {
    onChange(
      selectedSet.has(id) ? selected.filter((s) => s !== id) : [...selected, id]
    );
  };

  const removeById = (id) => onChange(selected.filter((s) => s !== id));

  const selectedCourses = courses.filter((c) => selectedSet.has(String(c._id)));

  return (
    <div className="mm-course-multiselect" ref={wrapRef}>
      {/* Selected chips */}
      {selectedCourses.length > 0 && (
        <div className="mm-course-chips">
          {selectedCourses.map((c) => (
            <span key={c._id} className="mm-course-chip">
              {c.title}
              <button type="button" className="mm-course-chip-remove"
                onClick={() => removeById(String(c._id))} aria-label="Remove">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="3" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Typeahead input */}
      <div className={`mm-typeahead-input-wrap ${open ? 'focused' : ''}`}>
        <input
          ref={inputRef}
          className="mm-typeahead-input"
          type="text"
          placeholder={courses.length === 0 ? 'Select subjects first…' : 'Search courses…'}
          value={query}
          disabled={courses.length === 0}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => { /* keep open for mousedown */ }}
          autoComplete="off"
        />
        <svg className="mm-typeahead-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {open && courses.length > 0 && (
        <ul className="mm-typeahead-dropdown">
          {filtered.length === 0 ? (
            <li className="mm-typeahead-empty">No courses match "{query}"</li>
          ) : (
            filtered.map((c) => {
              const checked = selectedSet.has(String(c._id));
              return (
                <li key={c._id}
                  className={`mm-multiselect-option ${checked ? 'checked' : ''}`}
                  onMouseDown={(e) => { e.preventDefault(); toggle(String(c._id)); }}>
                  <span className={`mm-checkbox ${checked ? 'checked' : ''}`}>
                    {checked && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </span>
                  <span>
                    <span className="mm-typeahead-name">{c.title}</span>
                    <span className="mm-typeahead-meta">Grade {c.grade} · {c.subject}</span>
                  </span>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}

// ─── AddMentorCard ────────────────────────────────────────────────────────────

function AddMentorCard({ onAdded }) {
  const [form, setForm]             = useState({ firstName: '', lastName: '', email: '', payPerSession: '' });
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [state, setState]           = useState({ loading: false, error: '', done: false });

  // Course catalogue
  const [allCourses, setAllCourses]         = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);

  useEffect(() => {
    listCourses()
      .then((d) => setAllCourses(d?.courses || []))
      .catch(() => {})
      .finally(() => setCoursesLoading(false));
  }, []);

  // All unique subjects from catalogue
  const allSubjects = [...new Set(allCourses.map((c) => c.subject))].sort();

  // Courses filtered to the selected subjects
  const subjectFilteredCourses = selectedSubjects.length === 0
    ? []
    : allCourses.filter((c) => selectedSubjects.includes(c.subject));

  // When subjects change, drop any selected courses no longer in scope
  const handleSubjectsChange = (subjects) => {
    setSelectedSubjects(subjects);
    if (subjects.length === 0) {
      setSelectedCourseIds([]);
    } else {
      const validIds = new Set(
        allCourses.filter((c) => subjects.includes(c.subject)).map((c) => String(c._id))
      );
      setSelectedCourseIds((prev) => prev.filter((id) => validIds.has(id)));
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: '', done: false });
    try {
      const data = await createTeacher({
        ...form,
        subjects: selectedSubjects,
        courseIds: selectedCourseIds,
      });
      setState({ loading: false, error: '', done: true });
      setForm({ firstName: '', lastName: '', email: '', payPerSession: '' });
      setSelectedSubjects([]);
      setSelectedCourseIds([]);
      onAdded?.(data?.user);
    } catch (err) {
      setState({ loading: false, error: err.message || 'Unable to add mentor.', done: false });
    }
  };

  return (
    <div className="mm-card">
      <p className="eyebrow">Add mentor</p>

      {state.done ? (
        <div className="mm-success-banner">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <p>Mentor added successfully.</p>
          <button className="secondary-btn compact" onClick={() => setState({ loading: false, error: '', done: false })}>
            Add another
          </button>
        </div>
      ) : (
        <form className="mm-form mm-inline-form-grid" onSubmit={handleSubmit}>
          <div className="mm-inline-row">
            <label className="mm-inline-label">First Name <span className="mm-required">*</span></label>
            <input className="mm-input" type="text" placeholder="First name"
              value={form.firstName} onChange={(e) => set('firstName', e.target.value)} required />
          </div>
          <div className="mm-inline-row">
            <label className="mm-inline-label">Last Name <span className="mm-required">*</span></label>
            <input className="mm-input" type="text" placeholder="Last name"
              value={form.lastName} onChange={(e) => set('lastName', e.target.value)} required />
          </div>
          <div className="mm-inline-row">
            <label className="mm-inline-label">Email <span className="mm-required">*</span></label>
            <input className="mm-input" type="email" placeholder="Email address"
              value={form.email} onChange={(e) => set('email', e.target.value)} required />
          </div>
          <div className="mm-inline-row">
            <label className="mm-inline-label">Pay Per Session <span className="mm-required">*</span></label>
            <input className="mm-input" type="number" min="0" placeholder="e.g. 50"
              value={form.payPerSession} onChange={(e) => set('payPerSession', e.target.value)} required />
          </div>

          <div className="mm-inline-row mm-inline-row--top">
            <label className="mm-inline-label">Subjects</label>
            {coursesLoading ? (
              <p className="mm-muted">Loading subjects…</p>
            ) : (
              <SubjectMultiSelect
                allSubjects={allSubjects}
                selected={selectedSubjects}
                onChange={handleSubjectsChange}
              />
            )}
          </div>

          <div className="mm-inline-row mm-inline-row--top">
            <label className="mm-inline-label">Courses</label>
            <CourseMultiSelect
              courses={subjectFilteredCourses}
              selected={selectedCourseIds}
              onChange={setSelectedCourseIds}
            />
          </div>

          <div className="mm-form-foot">
            <button className="mm-dark-btn" type="submit"
              disabled={state.loading || !form.firstName || !form.lastName || !form.email || !form.payPerSession}>
              {state.loading ? <LoadingDots /> : 'Add mentor'}
            </button>
            {state.error && <p className="error-text">{state.error}</p>}
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function ManageMentorsPage() {
  const currentUser = useCurrentUser();

  const [selectedMentorId, setSelectedMentorId] = useState('');
  const [activeMentorId, setActiveMentorId]     = useState('');
  const [activeMentorAssignments, setActiveMentorAssignments] = useState(0);
  const [searchError, setSearchError]           = useState('');
  const [lookupAttempted, setLookupAttempted]   = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  const [mentors, setMentors]               = useState([]);
  const [mentorsLoading, setMentorsLoading] = useState(true);

  const loadMentors = () => {
    setMentorsLoading(true);
    getMentorList()
      .then((d) => setMentors(d?.users || []))
      .catch(() => {})
      .finally(() => setMentorsLoading(false));
  };

  useEffect(() => { loadMentors(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedMentorId && !mentors.some((m) => m.id === selectedMentorId)) {
      setSelectedMentorId('');
    }
    if (activeMentorId && !mentors.some((m) => m.id === activeMentorId)) {
      setActiveMentorId('');
      setLookupAttempted(false);
      setActiveMentorAssignments(0);
    }
  }, [mentors, selectedMentorId, activeMentorId]);

  const handleLookup = () => {
    setLookupAttempted(true);
    setSearchError('');
    if (!selectedMentorId) {
      setActiveMentorId('');
      setActiveMentorAssignments(0);
      setSearchError('Select a mentor from the list to load their details.');
      return;
    }
    const exists = mentors.some((m) => m.id === selectedMentorId);
    if (!exists) {
      setActiveMentorId('');
      setActiveMentorAssignments(0);
      setSearchError('Mentor not found. Refresh the list and try again.');
      return;
    }
    setActiveMentorId(selectedMentorId);
    setActiveMentorAssignments(0);
  };

  const activeMentor = mentors.find((m) => m.id === activeMentorId) || null;
  const activeMentorName = activeMentor
    ? activeMentor.name || `${activeMentor.firstName || ''} ${activeMentor.lastName || ''}`.trim() || activeMentor.email
    : '';
  const foundMentor  = Boolean(activeMentor);

  if (currentUser?.role === 'student') {
    return (
      <div className="manage-mentors-page">
        <div className="mm-card">
          <p className="eyebrow">Access restricted</p>
          <p className="mm-muted">Please sign in with an admin or ops account to access this workspace.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-mentors-page">

      {/* ── Top row: two separate panels ── */}
      <div className="mm-ops-split">

        {/* Left: mentor lookup */}
        <div className="mm-card mm-search-card mm-ops-split-col">
          <p className="eyebrow">Mentor ops workspace</p>
          <p className="mm-search-note">
            Select a mentor from the directory to view their assignments and manage their status,
            or assign mentors to pending enrollments below.
          </p>
          <SearchRow
            mentors={mentors}
            value={selectedMentorId}
            onChange={(id) => {
              setSelectedMentorId(id);
              setLookupAttempted(false);
              setSearchError('');
              if (!id) {
                setActiveMentorId('');
                setActiveMentorAssignments(0);
              }
            }}
            loading={mentorsLoading}
            onLookup={handleLookup}
          >
            {lookupAttempted && foundMentor && (
              <div className="mm-search-status-row">
                <div className="mm-found-pill">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Viewing {activeMentorName || 'mentor'}
                </div>
              </div>
            )}
          </SearchRow>
          {searchError && <p className="error-text mm-mt-sm">{searchError}</p>}
        </div>

        {/* Right: schedule sessions */}
        <button
          type="button"
          className="mm-card mm-schedule-card mm-ops-split-col"
          onClick={() => setShowScheduleModal(true)}
        >
          <p className="eyebrow">Manage Schedules</p>
          <p className="mm-search-note">Create a class session on behalf of a mentor.</p>
          <div className="mm-schedule-panel-trigger">
            <div className="mm-schedule-panel-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
                <line x1="12" y1="14" x2="12" y2="18" />
                <line x1="10" y1="16" x2="14" y2="16" />
              </svg>
            </div>
            <p className="mm-schedule-panel-title">Schedule a Session</p>
            <svg className="mm-schedule-panel-arrow" width="16" height="16" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>
        </button>

      </div>

      {showScheduleModal && (
        <ScheduleMeetingModal
          meetingType="class"
          onClose={() => setShowScheduleModal(false)}
          onCreated={() => setShowScheduleModal(false)}
        />
      )}

      {foundMentor ? (
        <div className="mm-panel-grid">
          <div className="mm-panel-col">
            <MentorProfileCard
              mentor={activeMentor}
              canRemove={activeMentorAssignments === 0}
              onRemoved={() => {
                setActiveMentorId('');
                setSelectedMentorId('');
                setLookupAttempted(false);
                setSearchError('');
                setActiveMentorAssignments(0);
                loadMentors();
              }}
            />
          </div>
          <div className="mm-panel-col">
            <MentorAssignmentsCard
              teacherId={activeMentor.id}
              onCountChange={setActiveMentorAssignments}
              onRosterChange={() => loadMentors()}
            />
          </div>
        </div>
      ) : (
        <div className="mm-panel-grid">
          <div className="mm-panel-col">
            <UnassignedEnrollmentsCard
              mentors={mentors}
              mentorsLoading={mentorsLoading}
              onAssigned={() => loadMentors()}
            />
          </div>
          <div className="mm-panel-col">
            <AddMentorCard onAdded={() => loadMentors()} />
          </div>
        </div>
      )}

    </div>
  );
}

export default ManageMentorsPage;
